import { useEffect, useRef, useState } from 'react';
import { Button as Bit } from '@game/shared';
import { cn } from '../lib/cn.js';
import { useGameStore } from '../game/store.js';

/**
 * The on-screen buttons for phones and tablets. The joystick and look-drag
 * live on the canvas (`game/touch.ts`, imperative); these are the discrete
 * actions — jump, fire, crouch, reload — which are a perfect fit for React
 * because they change state a few times a second at most.
 *
 * Every control pokes a button bit into the engine via `onButton`; from there
 * the command pipeline is identical to keyboard play. Fire doubles as the
 * death cam's "next teammate" while eliminated, exactly like the left mouse.
 */

const buttonBase =
  'pointer-events-auto touch-none select-none rounded-full border border-white/20 ' +
  'bg-surface-900/60 font-bold uppercase tracking-wider text-ink/90 backdrop-blur ' +
  'active:scale-95 active:bg-white/25 transition-transform';

/** Held while touched, like a keyboard key. */
const HoldButton = ({
  bit,
  label,
  className,
  onButton,
}: {
  bit: number;
  label: string;
  className?: string;
  onButton: (bit: number, held: boolean) => void;
}) => (
  <button
    // Lets a drag that starts here still steer the camera (see game/touch.ts) —
    // the button cluster is exactly where the right thumb lives, and holding
    // fire while dragging to track a target is the genre's core gesture.
    data-look-through=""
    className={cn(buttonBase, className)}
    onPointerDown={(event) => {
      event.preventDefault();
      // Capture so the release always lands here even if the finger (or the
      // ?touch test mouse) has dragged off to aim — otherwise the bit would
      // stay held forever. Touch pointers are captured implicitly; this makes
      // the mouse behave the same.
      event.currentTarget.setPointerCapture(event.pointerId);
      onButton(bit, true);
    }}
    onPointerUp={() => onButton(bit, false)}
    onPointerCancel={() => onButton(bit, false)}
    onContextMenu={(event) => event.preventDefault()}
  >
    {label}
  </button>
);

/**
 * A tap that pulses its bit for a few ticks — for the slide, which the sim
 * commits to on the press edge and runs to completion by itself. Holding the
 * bit would drop the player into a crouch-walk once the slide ends.
 */
const TapButton = ({
  bit,
  label,
  className,
  onButton,
}: {
  bit: number;
  label: string;
  className?: string;
  onButton: (bit: number, held: boolean) => void;
}) => {
  const release = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Never leave the bit stuck if the HUD unmounts between pulse and release.
  useEffect(
    () => () => {
      if (release.current) clearTimeout(release.current);
    },
    [],
  );
  return (
    <button
      data-look-through=""
      className={cn(buttonBase, className)}
      onPointerDown={(event) => {
        event.preventDefault();
        onButton(bit, true);
        if (release.current) clearTimeout(release.current);
        // Long enough for several 64 Hz ticks to sample the press edge.
        release.current = setTimeout(() => onButton(bit, false), 150);
      }}
      onContextMenu={(event) => event.preventDefault()}
    >
      {label}
    </button>
  );
};

/**
 * The crouch latch. Tap to latch, tap to release — but its state lives in the
 * game store, not here, because the engine unlatches it by itself when a slide
 * ends: a tap while moving means "slide", and only a tap while standing still
 * is a crouch that should persist.
 */
const CrouchButton = ({
  className,
  onButton,
}: {
  className?: string;
  onButton: (bit: number, held: boolean) => void;
}) => {
  const on = useGameStore((state) => state.touchCrouch);
  return (
    <button
      data-look-through=""
      className={cn(
        buttonBase,
        on && 'border-team-blue/70 bg-team-blue/30 text-white',
        className,
      )}
      onPointerDown={(event) => {
        event.preventDefault();
        onButton(Bit.Crouch, !on);
      }}
      onContextMenu={(event) => event.preventDefault()}
    >
      Crouch
    </button>
  );
};

/** Tap to latch, tap to release — for ADS, where holding is a strain. */
const ToggleButton = ({
  bit,
  label,
  className,
  onButton,
}: {
  bit: number;
  label: string;
  className?: string;
  onButton: (bit: number, held: boolean) => void;
}) => {
  const [on, setOn] = useState(false);
  return (
    <button
      data-look-through=""
      className={cn(
        buttonBase,
        on && 'border-team-blue/70 bg-team-blue/30 text-white',
        className,
      )}
      onPointerDown={(event) => {
        event.preventDefault();
        setOn(!on);
        onButton(bit, !on);
      }}
      onContextMenu={(event) => event.preventDefault()}
    >
      {label}
    </button>
  );
};

export const TouchHud = ({
  onButton,
  onCycleWeapon,
  onPause,
}: {
  onButton: (bit: number, held: boolean) => void;
  onCycleWeapon: () => void;
  onPause: () => void;
}) => {
  const inHub = useGameStore((state) => state.inHub);
  const scoreboardOpen = useGameStore((state) => state.scoreboardOpen);
  const setScoreboardOpen = useGameStore((state) => state.setScoreboardOpen);

  // If this HUD unmounts mid-press (pause overlay, phase change), the engine
  // must not be left holding a phantom button.
  const onButtonRef = useRef(onButton);
  onButtonRef.current = onButton;
  useEffect(
    () => () => {
      for (const bit of [Bit.Fire, Bit.Jump, Bit.Crouch, Bit.Ads, Bit.Reload]) {
        onButtonRef.current(bit, false);
      }
    },
    [],
  );

  return (
    <div className="pointer-events-none fixed inset-0 z-10">
      <div className="absolute left-3 top-3 flex gap-2">
        <button
          className={cn(buttonBase, 'size-10 text-sm')}
          onPointerDown={(event) => {
            event.preventDefault();
            onPause();
          }}
        >
          ⏸
        </button>
        {!inHub && (
          <button
            className={cn(
              buttonBase,
              'size-10 text-sm',
              scoreboardOpen && 'bg-white/25',
            )}
            onPointerDown={(event) => {
              event.preventDefault();
              setScoreboardOpen(!scoreboardOpen);
            }}
          >
            ☰
          </button>
        )}
      </div>

      {inHub ? (
        <div className="absolute bottom-8 right-8 flex flex-col items-center gap-3">
          {/* Slide is a crouch pulse while moving — one tap, full slide. */}
          <TapButton
            bit={Bit.Crouch}
            label="Slide"
            className="size-14 text-[10px]"
            onButton={onButton}
          />
          <HoldButton bit={Bit.Jump} label="Jump" className="size-20 text-xs" onButton={onButton} />
        </div>
      ) : (
        <div className="absolute bottom-4 right-4 flex items-end gap-3">
          <div className="flex flex-col items-center gap-3">
            <CrouchButton className="size-14 text-[10px]" onButton={onButton} />
            {/* Tap to slide mid-run: the sim commits to the full slide from
                the press edge, so this only pulses the crouch bit. */}
            <TapButton
              bit={Bit.Crouch}
              label="Slide"
              className="size-14 text-[10px]"
              onButton={onButton}
            />
            <HoldButton
              bit={Bit.Jump}
              label="Jump"
              className="size-[4.5rem] text-xs"
              onButton={onButton}
            />
          </div>
          <div className="flex flex-col items-center gap-3">
            <div className="flex gap-3">
              <button
                data-look-through=""
                className={cn(buttonBase, 'size-12 text-[10px]')}
                onPointerDown={(event) => {
                  event.preventDefault();
                  onCycleWeapon();
                }}
                onContextMenu={(event) => event.preventDefault()}
              >
                Swap
              </button>
              <HoldButton
                bit={Bit.Reload}
                label="Reload"
                className="size-12 text-[10px]"
                onButton={onButton}
              />
            </div>
            <ToggleButton
              bit={Bit.Ads}
              label="Aim"
              className="size-14 text-[10px]"
              onButton={onButton}
            />
            <HoldButton
              bit={Bit.Fire}
              label="Fire"
              className="size-24 border-team-red/50 bg-team-red/25 text-sm"
              onButton={onButton}
            />
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * A phone held upright cannot fit a thumbstick, a fire cluster and an arena.
 * Every mobile shooter forces landscape; the web cannot, so it asks.
 */
export const RotateHint = () => {
  const [portrait, setPortrait] = useState(
    () => window.matchMedia('(orientation: portrait)').matches,
  );

  useEffect(() => {
    const query = window.matchMedia('(orientation: portrait)');
    const onChange = (event: MediaQueryListEvent) => setPortrait(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  if (!portrait) return null;
  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-surface-900/95 text-center">
      <div>
        <div className="text-5xl">📱↺</div>
        <div className="mt-4 text-lg font-bold">Rotate your device</div>
        <div className="mt-1 text-sm text-ink-muted">
          The arena needs landscape — turn your phone sideways.
        </div>
      </div>
    </div>
  );
};
